import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { BaseWidgetProps } from '../../types/dashboard';
import { formatDistanceToNow, subDays, startOfDay } from 'date-fns';

interface ActivityItem {
  id: string;
  type: 'call' | 'message' | 'inquiry' | 'case' | 'appointment' | 'document' | 'task';
  title: string;
  description: string;
  timestamp: string;
  user: {
    firstName: string;
    lastName: string;
    role: string;
  };
  metadata?: {
    clientName?: string;
    inquirySubject?: string;
    caseTitle?: string;
    duration?: number;
    outcome?: string;
  };
}

export function ActivityFeedWidget({
  widget,
  isEditing: _isEditing,
  onConfigChange,
  onRemove: _onRemove,
  className = '',
}: BaseWidgetProps) {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActivities();
    
    // Set up auto-refresh every 30 seconds
    const interval = setInterval(loadActivities, 30000);
    return () => clearInterval(interval);
  }, [widget.config.timeRange, widget.config.maxActivities]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      
      // Generate mock activity data for demonstration
      // In a real implementation, this would fetch from your API
      const mockActivities = generateMockActivities();
      
      // Filter by time range
      const filteredActivities = filterByTimeRange(mockActivities);
      
      // Limit results
      const limitedActivities = filteredActivities.slice(0, widget.config.maxActivities || 15);
      
      setActivities(limitedActivities);
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateMockActivities = (): ActivityItem[] => {
    const now = new Date();
    const activities: ActivityItem[] = [];
    
    // Generate activities for the last 7 days
    for (let i = 0; i < 50; i++) {
      const timestamp = new Date(now.getTime() - Math.random() * 7 * 24 * 60 * 60 * 1000);
      const types: ActivityItem['type'][] = ['call', 'message', 'inquiry', 'case', 'appointment', 'document', 'task'];
      const type = types[Math.floor(Math.random() * types.length)];
      
      const users = [
        { firstName: 'John', lastName: 'Smith', role: 'Attorney' },
        { firstName: 'Sarah', lastName: 'Johnson', role: 'Case Manager' },
        { firstName: 'Mike', lastName: 'Davis', role: 'Attorney' },
        { firstName: 'Lisa', lastName: 'Wilson', role: 'Paralegal' },
      ];
      const user = users[Math.floor(Math.random() * users.length)];
      
      let activity: ActivityItem;
      
      switch (type) {
        case 'call':
          activity = {
            id: `activity-${i}`,
            type,
            title: 'Phone call completed',
            description: `Called client about case update`,
            timestamp: timestamp.toISOString(),
            user,
            metadata: {
              clientName: 'Robert Anderson',
              duration: Math.floor(Math.random() * 1800) + 300, // 5-35 minutes
              outcome: ['answered', 'voicemail', 'no_answer'][Math.floor(Math.random() * 3)],
            },
          };
          break;
        case 'message':
          activity = {
            id: `activity-${i}`,
            type,
            title: 'Message sent',
            description: `Sent message to client regarding document requirements`,
            timestamp: timestamp.toISOString(),
            user,
            metadata: {
              clientName: 'Jennifer Martinez',
            },
          };
          break;
        case 'inquiry':
          activity = {
            id: `activity-${i}`,
            type,
            title: 'New inquiry received',
            description: `Personal injury inquiry from potential client`,
            timestamp: timestamp.toISOString(),
            user,
            metadata: {
              inquirySubject: 'Car accident claim',
            },
          };
          break;
        case 'case':
          activity = {
            id: `activity-${i}`,
            type,
            title: 'Case status updated',
            description: `Updated case status to "In Progress"`,
            timestamp: timestamp.toISOString(),
            user,
            metadata: {
              caseTitle: 'Smith vs. Insurance Co.',
            },
          };
          break;
        case 'appointment':
          activity = {
            id: `activity-${i}`,
            type,
            title: 'Appointment scheduled',
            description: `Scheduled conference with new client`,
            timestamp: timestamp.toISOString(),
            user,
            metadata: {
              clientName: 'David Thompson',
            },
          };
          break;
        case 'document':
          activity = {
            id: `activity-${i}`,
            type,
            title: 'Document uploaded',
            description: `Uploaded medical records to case file`,
            timestamp: timestamp.toISOString(),
            user,
            metadata: {
              caseTitle: 'Johnson Personal Injury',
            },
          };
          break;
        case 'task':
          activity = {
            id: `activity-${i}`,
            type,
            title: 'Task completed',
            description: `Completed document review task`,
            timestamp: timestamp.toISOString(),
            user,
          };
          break;
        default:
          continue;
      }
      
      activities.push(activity);
    }
    
    return activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  };

  const filterByTimeRange = (activities: ActivityItem[]): ActivityItem[] => {
    const now = new Date();
    let startDate: Date;
    
    switch (widget.config.timeRange) {
      case 'today':
        startDate = startOfDay(now);
        break;
      case 'week':
        startDate = subDays(now, 7);
        break;
      case 'month':
        startDate = subDays(now, 30);
        break;
      default:
        startDate = subDays(now, 7);
    }
    
    return activities.filter(activity => 
      new Date(activity.timestamp) >= startDate
    );
  };

  const getActivityIcon = (type: ActivityItem['type']) => {
    const iconClass = "w-4 h-4";
    
    switch (type) {
      case 'call':
        return (
          <svg className={`${iconClass} text-blue-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        );
      case 'message':
        return (
          <svg className={`${iconClass} text-green-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        );
      case 'inquiry':
        return (
          <svg className={`${iconClass} text-purple-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
        );
      case 'case':
        return (
          <svg className={`${iconClass} text-red-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'appointment':
        return (
          <svg className={`${iconClass} text-yellow-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case 'document':
        return (
          <svg className={`${iconClass} text-indigo-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        );
      case 'task':
        return (
          <svg className={`${iconClass} text-orange-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        );
      default:
        return (
          <svg className={`${iconClass} text-gray-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
    }
  };

  const getActivityColor = (type: ActivityItem['type']) => {
    const colors: Record<ActivityItem['type'], string> = {
      call: 'text-blue-600 bg-blue-50',
      message: 'text-green-600 bg-green-50',
      inquiry: 'text-purple-600 bg-purple-50',
      case: 'text-red-600 bg-red-50',
      appointment: 'text-yellow-600 bg-yellow-50',
      document: 'text-indigo-600 bg-indigo-50',
      task: 'text-orange-600 bg-orange-50',
    };
    return colors[type] || 'text-gray-600 bg-gray-50';
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <Card className={`h-full ${className}`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="w-5 h-5 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Activity Feed
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`h-full ${className}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <svg className="w-5 h-5 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Activity Feed
          </CardTitle>
          <div className="flex gap-1">
            <Button
              variant={widget.config.timeRange === 'today' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onConfigChange?.({ ...widget.config, timeRange: 'today' })}
              className="h-7 text-xs"
            >
              Today
            </Button>
            <Button
              variant={widget.config.timeRange === 'week' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onConfigChange?.({ ...widget.config, timeRange: 'week' })}
              className="h-7 text-xs"
            >
              Week
            </Button>
            <Button
              variant={widget.config.timeRange === 'month' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onConfigChange?.({ ...widget.config, timeRange: 'month' })}
              className="h-7 text-xs"
            >
              Month
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {activities.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <h3 className="text-sm font-medium text-gray-900 mb-1">No recent activity</h3>
              <p className="text-xs text-gray-600">Activity will appear here as it happens</p>
            </div>
          ) : (
            activities.map((activity, index) => (
              <div key={activity.id} className="flex items-start gap-3">
                {/* Timeline */}
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center flex-shrink-0">
                    {getActivityIcon(activity.type)}
                  </div>
                  {index < activities.length - 1 && (
                    <div className="w-px h-8 bg-gray-200 mt-2"></div>
                  )}
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-aalaw-charcoal text-sm">
                          {activity.title}
                        </h4>
                        <Badge className={`text-xs ${getActivityColor(activity.type)}`}>
                          {activity.type}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-gray-700 mb-2">
                        {activity.description}
                      </p>
                      
                      {/* Metadata */}
                      {activity.metadata && (
                        <div className="flex flex-wrap gap-3 text-xs text-gray-600 mb-2">
                          {activity.metadata.clientName && (
                            <span className="flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              {activity.metadata.clientName}
                            </span>
                          )}
                          {activity.metadata.duration && (
                            <span className="flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {formatDuration(activity.metadata.duration)}
                            </span>
                          )}
                          {activity.metadata.outcome && (
                            <span className="flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              {activity.metadata.outcome}
                            </span>
                          )}
                          {activity.metadata.inquirySubject && (
                            <span className="flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              {activity.metadata.inquirySubject}
                            </span>
                          )}
                          {activity.metadata.caseTitle && (
                            <span className="flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              {activity.metadata.caseTitle}
                            </span>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span>{activity.user.firstName} {activity.user.lastName}</span>
                        <span>•</span>
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="flex items-center justify-between text-xs text-gray-600">
            <span>Showing {activities.length} {activities.length === 1 ? 'activity' : 'activities'}</span>
            <Button onClick={loadActivities} variant="ghost" size="sm" className="h-7 text-xs">
              <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}